import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Eye, EyeOff, ArrowRight, Shield, Chrome, Linkedin } from 'lucide-react'
import { FeatureShowcase } from './FeatureShowcase'
import { cn } from '@/lib/utils'
import { Typewriter } from '@/components/Typewriter'
import { getAuthErrorMessage } from '@/lib/auth-errors';


export function LoginForm() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [mounted, setMounted] = useState(false)
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !loading) {
        e.preventDefault()
        submitButtonRef.current?.click()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [loading])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      navigate('/')
    } catch (error) {
      setError(getAuthErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (error) throw error
    } catch (error) {
      setError(getAuthErrorMessage(error))
    }
  }

  const handleLinkedInLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (error) throw error
    } catch (error) {
      setError(getAuthErrorMessage(error))
    }
  }

  return (
    <div className="min-h-screen w-full flex">
      {/* ───── Left Panel — Gradient Background ───── */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Base gradient background */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, #06101f 0%, #0a1a2e 25%, #0f1f35 50%, #0b1628 75%, #080e1f 100%)'
        }} />

        {/* Mesh gradient layer */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 80% 60% at 15% 20%, hsl(213 80% 64% / 0.12) 0%, transparent 60%),
            radial-gradient(ellipse 60% 80% at 80% 75%, hsl(265 75% 68% / 0.1) 0%, transparent 55%),
            radial-gradient(ellipse 50% 40% at 50% 50%, hsl(200 80% 40% / 0.06) 0%, transparent 50%)
          `
        }} />

        {/* Animated glow orb — bleu */}
        <div
          className="absolute top-[18%] left-[10%] w-[350px] h-[350px] rounded-full blur-[140px]"
          style={{
            background: 'hsl(213 80% 64% / 0.15)',
            animation: 'subtleFloat 8s ease-in-out infinite',
          }}
        />
        {/* Animated glow orb — purple */}
        <div
          className="absolute bottom-[10%] right-[5%] w-[300px] h-[300px] rounded-full blur-[120px]"
          style={{
            background: 'hsl(265 75% 68% / 0.12)',
            animation: 'subtleFloat 10s ease-in-out infinite',
            animationDelay: '2s',
          }}
        />
        {/* Animated glow orb — teal accent */}
        <div
          className="absolute top-[55%] right-[35%] w-[200px] h-[200px] rounded-full blur-[100px]"
          style={{
            background: 'hsl(195 80% 45% / 0.08)',
            animation: 'subtleFloat 12s ease-in-out infinite',
            animationDelay: '4s',
          }}
        />

        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />

        {/* Top-left radial highlight */}
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full blur-[150px] opacity-20"
          style={{ background: 'hsl(213 80% 64% / 0.2)' }}
        />

        {/* Content overlay */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo / Brand top */}
          <div
            className={cn(
              "flex items-center gap-3 transition-all duration-700",
              mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
            )}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(213,80%,64%)] to-[hsl(265,75%,68%)] flex items-center justify-center shadow-lg shadow-[hsl(213,80%,64%)]/30">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/90 text-lg font-bold tracking-tight font-['Fraunces']">CallCenter</span>
          </div>

          {/* Central content */}
          <div
            className={cn(
              "space-y-8 max-w-lg transition-all duration-700 delay-200",
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <div className="space-y-4">
              <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.1] tracking-tight font-['Fraunces'] min-h-[2.2em]">
                Pilotez votre
                <br />
                <Typewriter
                  className="bg-gradient-to-r from-[hsl(213,80%,64%)] to-[hsl(265,75%,68%)] bg-clip-text text-transparent inline-block min-h-[1em]"
                  phrases={[
                    "centre d'appels",
                    "relation client",
                    "performance",
                    "analyse IA"
                  ]}
                  cursorColor="hsl(185, 90%, 52%)"
                />
              </h1>
              <p className="text-white/50 text-base leading-relaxed max-w-md">
                Analyses intelligentes, suivi des performances et insights en temps réel pour optimiser vos opérations.
              </p>
            </div>

            <FeatureShowcase mounted={mounted} />
          </div>

          {/* Bottom quote */}
          <div
            className={cn(
              "transition-all duration-700 delay-500",
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >

          </div>
        </div>
      </div>

      {/* ───── Right Panel — Login Form ───── */}
      <div className="w-full lg:w-[45%] flex items-center justify-center relative bg-background">
        {/* Subtle background texture */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.015]" />

        {/* Subtle gradient accent */}
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        {/* Form container */}
        <div
          className={cn(
            "w-full max-w-[420px] px-8 py-12 transition-all duration-700 delay-300",
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          )}
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-foreground text-xl font-bold tracking-tight font-['Fraunces']">CallCenter</span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-foreground font-['Fraunces']">
              Connexion
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Accédez à votre espace de gestion
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-medium text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@exemple.com"
                className={cn(
                  "h-11 text-sm transition-all duration-300 rounded-xl",
                  "bg-muted/40 border-border/30",
                  "focus:border-primary/50 focus:ring-2 focus:ring-primary/15 focus:bg-muted/60",
                  "hover:border-primary/30"
                )}
                required
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-xs font-medium text-muted-foreground">
                  Mot de passe
                </label>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-[11px] text-primary/70 hover:text-primary font-medium"
                  onClick={() => navigate('/auth/forgot-password')}
                >
                  Oublié ?
                </Button>
              </div>
              <div className="relative group">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={cn(
                    "pr-10 h-11 text-sm transition-all duration-300 rounded-xl",
                    "bg-muted/40 border-border/30",
                    "focus:border-primary/50 focus:ring-2 focus:ring-primary/15 focus:bg-muted/60",
                    "hover:border-primary/30"
                  )}
                  required
                />
                <div className="absolute right-1 inset-y-0 flex items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-muted/60 rounded-lg"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-foreground transition-colors" />
                    ) : (
                      <Eye className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-foreground transition-colors" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <Alert variant="destructive" className="animate-slide-in border-destructive/30 bg-destructive/10 rounded-xl">
                <AlertDescription className="text-sm text-destructive">{error}</AlertDescription>
              </Alert>
            )}

            {/* Submit */}
            <Button
              ref={submitButtonRef}
              type="submit"
              className={cn(
                "w-full h-11 text-sm font-semibold transition-all duration-300 rounded-xl mt-2",
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "hover:shadow-lg hover:shadow-primary/25 active:scale-[0.98]",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Connexion...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  Se connecter
                  <ArrowRight className="w-4 h-4" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/15" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-background px-3 text-muted-foreground/40 font-medium tracking-widest">
                ou
              </span>
            </div>
          </div>

          {/* OAuth Buttons */}
          <div className="grid grid-cols-2 gap-3">
            {/* Google */}
            <Button
              variant="outline"
              type="button"
              onClick={handleGoogleLogin}
              className={cn(
                "h-11 text-sm font-medium rounded-xl",
                "bg-muted/20 border-border/20 hover:border-primary/30",
                "hover:bg-muted/40 transition-all duration-300"
              )}
            >
              <Chrome className="w-4 h-4 mr-2" />
              Google
            </Button>

            {/* LinkedIn */}
            <Button
              variant="outline"
              type="button"
              onClick={handleLinkedInLogin}
              className={cn(
                "h-11 text-sm font-medium rounded-xl",
                "bg-muted/20 border-border/20 hover:border-primary/30",
                "hover:bg-muted/40 transition-all duration-300"
              )}
            >
              <Linkedin className="w-4 h-4 mr-2 text-[#0077b5]" />
              LinkedIn
            </Button>
          </div>

          {/* Sign up link */}
          <p className="text-center text-xs text-muted-foreground/60 mt-6">
            Pas de compte ?{' '}
            <button
              type="button"
              className="text-primary hover:text-primary/80 font-medium transition-colors"
              onClick={() => navigate('/')}
            >
              Tester la solution
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}